import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChefHat } from 'lucide-react';
import { S3_BASE_URL } from '../../utils/Constants';

const MealCard = ({ meal, i18n, compact = false, count = 1 }) => {
  if (compact) {
    return (
      <div className="bg-gray-50 rounded-md p-2">
        <div className="flex items-center gap-2">
          {meal.imageUrl && (
            <img
              src={S3_BASE_URL + '/' + meal.imageUrl}
              alt={meal.name}
              className="w-8 h-8 rounded-md object-cover"
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-900 truncate">
              {count > 1 && <span className="text-blue-600 font-bold">{count}x </span>}
              {i18n.language === 'ar' ? (meal.nameAr || meal.name) : (meal.nameEn || meal.name)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="flex items-start gap-3">
        {meal.imageUrl && (
          <img
            src={S3_BASE_URL + '/' + meal.imageUrl}
            alt={meal.name}
            className="w-12 h-12 rounded-lg object-cover"
          />
        )}
        <div className="flex-1 min-w-0">
          <h5 className="font-medium text-gray-900 truncate">
            {count > 1 && <span className="text-blue-600 font-bold">{count}x </span>}
            {i18n.language === 'ar' ? (meal.nameAr || meal.name) : (meal.nameEn || meal.name)}
          </h5>
        </div>
      </div>
    </div>
  );
};

const MealsList = ({ meals = [], i18n, compact = false }) => {
  const { t } = useTranslation();

  // Group meals by ID and count occurrences
  const groupedMeals = meals.reduce((acc, meal) => {
    const key = meal.id || meal.name; // Use ID if available, fallback to name
    if (acc[key]) {
      acc[key].count += 1;
    } else {
      acc[key] = { meal, count: 1 };
    }
    return acc;
  }, {});

  const uniqueMeals = Object.values(groupedMeals);

  if (compact) {
    return (
      <div>
        <h4 className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
          <ChefHat className="w-3 h-3" />
          {t('meals', 'Meals')} ({meals.length})
        </h4>
        <div className="grid grid-cols-2 gap-2">
          {uniqueMeals.slice(0, 4).map(({ meal, count }, index) => (
            <MealCard key={meal.id || index} meal={meal} i18n={i18n} compact={true} count={count} />
          ))}
          {uniqueMeals.length > 4 && (
            <div className="bg-gray-100 rounded-md p-2 flex items-center justify-center">
              <p className="text-xs text-gray-500">+{uniqueMeals.length - 4} more</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
        <ChefHat className="w-4 h-4" />
        {t('meals', 'Meals')} ({meals.length})
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {uniqueMeals.map(({ meal, count }, index) => (
          <MealCard key={meal.id || index} meal={meal} i18n={i18n} compact={false} count={count} />
        ))}
      </div>
    </div>
  );
};

export default MealsList;
